import en from "@/locales/en-US.json";
import fr from "@/locales/fr-FR.json";
import nl_BE from "@/locales/nl-BE.json";
import nl_NL from "@/locales/nl-NL.json";
import de_DE from "@/locales/de-DE.json";
import de_CH from "@/locales/de-CH.json";
import it from "@/locales/it-IT.json";
import pt from "@/locales/pt-PT.json";

// Whitelist of locales the app knows how to render. To add a new locale:
//   1. drop the translation file into src/locales/ and import it above;
//   2. add an entry here with the imported messages and the label shown in the language switcher.
// Codes that appear in VUE_APP_ALLOWED_LOCALES but are missing from this map are silently ignored.
const SUPPORTED_LOCALES = {
  en: { messages: en, label: "English" },
  fr: { messages: fr, label: "Français" },
  nl_BE: { messages: nl_BE, label: "Flemish" },
  nl_NL: { messages: nl_NL, label: "Nederlands" },
  de_DE: { messages: de_DE, label: "Deutsch" },
  de_CH: { messages: de_CH, label: "Deutsch (Schweiz)" },
  it: { messages: it, label: "Italiano" },
  pt: { messages: pt, label: "Português" }
};

const DEFAULT_LOCALES = ["en", "fr", "nl_BE"];

export const FALLBACK_LOCALE = "en";

// VUE_APP_ALLOWED_LOCALES contract:
// - unset (undefined)            → defaults to ["en", "fr", "nl_BE"]
// - empty string ("")            → no locales available to switch (translation switcher hidden)
// - comma-separated list         → only those locales (intersected with SUPPORTED_LOCALES)
const parseEnvLocales = (raw) => {
  if (raw === undefined || raw === null) return DEFAULT_LOCALES;
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const ALLOWED_LOCALES = parseEnvLocales(process.env.VUE_APP_ALLOWED_LOCALES).filter((l) =>
  Object.prototype.hasOwnProperty.call(SUPPORTED_LOCALES, l)
);

// Always load fallback messages so $t() keeps working even if the fallback locale is not user-selectable.
const localesToLoad = Array.from(new Set([FALLBACK_LOCALE, ...ALLOWED_LOCALES]));

export const LOCALE_MESSAGES = localesToLoad.reduce((acc, l) => {
  acc[l] = SUPPORTED_LOCALES[l].messages;
  return acc;
}, {});

export const LOCALE_LABELS = Object.fromEntries(
  Object.entries(SUPPORTED_LOCALES).map(([code, { label }]) => [code, label])
);

export const migrateLocale = (locale) => (locale === "fl" ? "nl_BE" : locale);

export const isLocaleAllowed = (locale) => ALLOWED_LOCALES.includes(locale);

export const normalizeLocale = (locale) => {
  const migrated = migrateLocale(locale);
  if (isLocaleAllowed(migrated)) return migrated;
  if (ALLOWED_LOCALES.length > 0) return ALLOWED_LOCALES[0];
  return FALLBACK_LOCALE;
};
